import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    InputBase,
    TextField,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/core/styles';
import { getTokens } from '../../styles/tokens';
import useDialogStyles from '../TransferTicketModalCustom/dialogStyles';

const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    const isDark = theme.palette.type === 'dark';
    // mesmos tons da conversa (padrão do WhatsApp Web)
    const wa = isDark
        ? { field: '#202c33', focus: '#00a884', bg: '#0b141a', outgoing: '#005c4b', text: '#e9edef' }
        : { field: '#f0f2f5', focus: '#00a884', bg: '#efeae2', outgoing: '#d9fdd3', text: '#111b21' };
    return {
        searchBox: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 38,
            padding: '0 12px 0 14px',
            borderRadius: 19,
            backgroundColor: wa.field,
            border: '1px solid transparent',
            flex: 'none',
            '&:focus-within': {
                backgroundColor: t.paper,
                borderColor: wa.focus,
                boxShadow: `0 0 0 1px ${wa.focus}`,
            },
        },
        searchIcon: {
            color: t.textMuted,
            fontSize: 18,
            flex: 'none',
        },
        searchInput: {
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            color: t.text,
            '& input::placeholder': {
                color: t.textMuted,
                opacity: 1,
            },
        },
        list: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
        },
        templateItem: {
            display: 'block',
            width: '100%',
            padding: '10px 12px',
            border: `1px solid ${t.line}`,
            borderRadius: t.radius.md,
            background: t.paper,
            color: t.text,
            font: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'background-color 120ms ease, border-color 120ms ease',
            '&:hover': {
                backgroundColor: t.muted,
                borderColor: t.lineStrong,
            },
            '&:focus-visible': {
                outline: `2px solid ${wa.focus}`,
                outlineOffset: 1,
            },
        },
        templateInfo: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
        },
        templateName: {
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        tags: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            flex: 'none',
        },
        tag: {
            padding: '1px 7px',
            borderRadius: t.radius.badge,
            backgroundColor: t.status.neutral.bg,
            color: t.status.neutral.fg,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: '18px',
            whiteSpace: 'nowrap',
        },
        templateText: {
            marginTop: 6,
            fontSize: 13,
            lineHeight: '18px',
            color: t.textSecondary,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
        },
        // prévia do modelo como bolha enviada
        preview: {
            padding: 12,
            borderRadius: t.radius.md,
            backgroundColor: wa.bg,
            display: 'flex',
            justifyContent: 'flex-end',
        },
        bubble: {
            maxWidth: '92%',
            padding: '6px 9px 8px',
            borderRadius: 7.5,
            backgroundColor: wa.outgoing,
            color: wa.text,
            fontSize: 14,
            lineHeight: '19px',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            boxShadow: '0 1px 0.5px rgba(11,20,26,0.13)',
        },
        groupTitle: {
            margin: '4px 0 -4px',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: t.textMuted,
        },
    };
});

// nome de cada parte do modelo, para os grupos de variáveis
const COMPONENT_LABELS = {
    header: 'Cabeçalho',
    body: 'Corpo',
    footer: 'Rodapé',
    buttons: 'Botões',
};

const TemplateModal = ({ open, handleClose, templates, onSelectTemplate }) => {
    const classes = useStyles();
    const dialogClasses = useDialogStyles({ width: 560 });
    const [search, setSearch] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [variables, setVariables] = useState([]);
    const [variableValues, setVariableValues] = useState({});
    const [renderedContent, setRenderedContent] = useState('');

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
    };

    const extractVariablesByComponent = (components) => {
        const regex = /{{(\d+)}}/g;
        let variables = {
            header: [],
            body: [],
            footer: [],
            buttons: []
        };

        components.forEach((component) => {
            let match;
            const type = component.type?.toLowerCase();
            const text = component.text || '';

            if (type === 'header' && ['IMAGE', 'VIDEO'].includes(component?.format)) {
                variables[type].push({ type: `${component?.format.toLowerCase()}`, prompt: component?.format.toLowerCase() === 'image' ? 'Insira a URL da imagem' : 'Insira a URL do vídeo' });
            }
            // Para os botões, verifique a URL para variáveis
            if (type === 'buttons') {
                const buttons = JSON.parse(component.buttons)
                buttons.forEach((button, index) => {
                    if (button.example) {
                        console.log("button", button, index)
                        variables[type].push({ type: button.type, prompt: button.example, index: index });
                    }
                    else if (variables[type]) {
                        while ((match = regex.exec(text)) !== null) {
                            variables[type].push(match[0]);
                        }
                    }
                });
            } else if (variables[type]) {
                while ((match = regex.exec(text)) !== null) {
                    variables[type].push({ type: 'text', prompt: match[0] });
                }
            } else {
                console.warn(`Tipo de componente desconhecido: ${type}`);
            }
        });
        return variables;
    };



    const handleSelectTemplate = (template) => {
        const components = template?.components || [];
        const extractedVariables = extractVariablesByComponent(components);

        setSelectedTemplate(template);
        setVariables(extractedVariables);
        setRenderedContent(components.map((component) => component?.text).join(`\n`));
    };

    // const generateBodyToSave = (content, variables) => {
    //     let bodyToSave = content;
    //     console.log("variables", variables)
    //     console.log("bodyToSave", bodyToSave)
    //     Object.keys(variables).forEach((componentType) => {
    //         console.log("componentType", componentType)
    //         const componentVariables = variables[componentType];
    //         console.log("componentVariables", componentVariables, typeof componentVariables)
    //         // Verificar se componentVariables é um array
    //         if (Array.isArray(componentVariables)) {
    //             componentVariables.forEach((variable, index) => {
    //                 const placeholder = `{{${index + 1}}}`;
    //                 const value = variable?.value || '';
    //                 bodyToSave = bodyToSave.replace(placeholder, value);
    //             });
    //         } else if (typeof componentVariables === 'object') {
    //             Object.keys(componentVariables).forEach((key, index) => {
    //                 const placeholder = `{{${index + 1}}}`;
    //                 const value = componentVariables[key]?.value || '';
    //                 bodyToSave = bodyToSave.replace(placeholder, value);
    //             });
    //         } else {
    //             console.error(`Expected array or object for componentType: ${componentType}, but got`, componentVariables);
    //         }
    //     });
    //     console.log("bodyToSave", bodyToSave)

    //     return bodyToSave;
    // };
    const generateBodyToSave = (content, variables) => {
        let bodyToSave = content;
        Object.keys(variables).forEach((componentType) => {
            const componentVariables = variables[componentType];

            if (Array.isArray(componentVariables)) {
                console.log("componentVariables ARRAY", componentVariables)
                // Se for um array, iteramos sobre ele substituindo as variáveis
                componentVariables.forEach((variable, index) => {
                    const placeholder = `{{${index + 1}}}`;
                    const value = variable?.value || '';
                    bodyToSave = bodyToSave.replace(placeholder, value);
                });
            } else if (typeof componentVariables === 'object') {
                console.log("componentVariables OBJECT", componentVariables)
                // Se for um objeto (caso do header com uma imagem ou outro tipo de conteúdo)
                if (componentType === 'header') {
                    console.log("componentVariables", componentVariables)
                    // Substituir um placeholder específico no header por uma URL de imagem
                    Object.keys(componentVariables).forEach((key, index) => {
                        const value = componentVariables[key]?.value || '';
                        console.log("value", value, value.startsWith("http"))
                        if (value.startsWith("http")) {                            
                            bodyToSave = `${value} \n ${bodyToSave}`
                            console.log("bodyToSave", bodyToSave)
                        } else {
                            const placeholder = `{{${index + 1}}}`;

                            bodyToSave = bodyToSave.replace(placeholder, value);
                        }
                    });
                } else {
                    // Para texto ou outros tipos de componentes, substituímos as variáveis como antes
                    Object.keys(componentVariables).forEach((key, index) => {
                        const placeholder = `{{${index + 1}}}`;
                        const value = componentVariables[key]?.value || '';
                        bodyToSave = bodyToSave.replace(placeholder, value);
                    });
                }
            } else {
                console.error(`Expected array or object for componentType: ${componentType}, but got`, componentVariables);
            }
        });

        console.log("bodyToSave", bodyToSave);
        return bodyToSave;
    };

    const handleSendTemplate = () => {
        const bodyToSave = generateBodyToSave(renderedContent, variableValues);
        const templateWithVariables = {
            ...selectedTemplate,
            variables: variableValues,
            bodyToSave
        };
        onSelectTemplate(templateWithVariables);
    };

    const handleVariableChange = (componentType, index, value, buttonIndex) => {
        // Atualiza o estado das variáveis por tipo de componente
        const newComponentValues = { ...variableValues[componentType], [index]: { value, buttonIndex } };
        const newValues = { ...variableValues, [componentType]: newComponentValues };
        setVariableValues(newValues);
    };

    const filteredTemplates = templates.filter((template) =>
        template?.shortcode?.toLowerCase().includes(search?.toLowerCase())
    )

    const handleBack = () => {
        setSelectedTemplate(null);
        setVariableValues({});
        setRenderedContent('');
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            scroll="paper"
            classes={{ paper: dialogClasses.paper }}
            aria-labelledby="template-modal-title"
        >
            <div className={dialogClasses.header}>
                <h2 id="template-modal-title" className={dialogClasses.title}>
                    Templates do WhatsApp
                </h2>
                <p className={dialogClasses.subtitle}>
                    {selectedTemplate
                        ? `Modelo: ${selectedTemplate.name}`
                        : 'Escolha um modelo aprovado para enviar nesta conversa.'}
                </p>
            </div>
            {!selectedTemplate && (
                <DialogContent className={dialogClasses.content}>
                    <div className={classes.searchBox}>
                        <SearchIcon className={classes.searchIcon} />
                        <InputBase
                            placeholder="Pesquisar Templates"
                            value={search}
                            onChange={handleSearchChange}
                            className={classes.searchInput}
                            inputProps={{ 'aria-label': 'Pesquisar Templates' }}
                        />
                    </div>

                    {filteredTemplates.length === 0 ? (
                        <div className={dialogClasses.empty}>Nenhum template encontrado.</div>
                    ) : (
                        <div className={classes.list}>
                            {filteredTemplates.map((template, index) => (
                                <button
                                    type="button"
                                    key={index}
                                    className={classes.templateItem}
                                    onClick={() => handleSelectTemplate(template)}
                                >
                                    <span className={classes.templateInfo}>
                                        <span className={classes.templateName}>{template.shortcode}</span>
                                        <span className={classes.tags}>
                                            {template.language && (
                                                <span className={classes.tag} title="Idioma">{template.language}</span>
                                            )}
                                            {template.category && (
                                                <span className={classes.tag} title="Categoria">{template.category}</span>
                                            )}
                                        </span>
                                    </span>
                                    <span className={classes.templateText}>
                                        {template?.components?.map(component => component?.text).join(`\n`)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </DialogContent>
            )}
            {selectedTemplate && (
                <DialogContent className={dialogClasses.content}>
                    <div className={classes.preview}>
                        <div className={classes.bubble}>{renderedContent}</div>
                    </div>
                    <div className={classes.tags}>
                        <span className={classes.tag}>Idioma: {selectedTemplate.language}</span>
                        <span className={classes.tag}>Categoria: {selectedTemplate.category}</span>
                    </div>
                    {/* Renderização dos campos para preencher as variáveis */}
                    {Object.keys(variables).map((componentType, index) => (
                        <React.Fragment key={`${componentType}-${index}`}>
                            {variables[componentType].length > 0 && (
                                <>
                                    <div className={classes.groupTitle}>
                                        {COMPONENT_LABELS[componentType] || componentType}
                                    </div>
                                    {variables[componentType].map((variable, index) => (
                                        <div className={dialogClasses.field} key={`${componentType}-${index}`}>
                                            <span className={dialogClasses.label}>{`${variable?.prompt}`}</span>
                                            <TextField
                                                value={variableValues[componentType]?.[index]?.value || ''}
                                                onChange={(e) => handleVariableChange(componentType, index, e.target.value, variable?.index || 0)}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                inputProps={{ 'aria-label': `${variable?.prompt}` }}
                                            />
                                        </div>
                                    ))}
                                </>
                            )}
                        </React.Fragment>
                    ))}
                </DialogContent>
            )}
            <DialogActions className={dialogClasses.actions}>
                {selectedTemplate ? (
                    <>
                        <Button onClick={handleBack} className={dialogClasses.cancel}>
                            Voltar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSendTemplate}
                            className={dialogClasses.confirm}
                        >
                            Enviar
                        </Button>
                    </>
                ) : (
                    <Button onClick={handleClose} className={dialogClasses.cancel}>
                        Cancelar
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default TemplateModal;
